/**
 * @file Writes the parsed-email sidecar (`parsedEmail.json`) into a temp file
 * folder. The acceptance-time callable (`persistAcceptedExtraction`) decides an
 * extraction has an email source by reading this sidecar at the EXTRACTED
 * file's folder. The agent never extracts the `.eml` itself (the email body is
 * surfaced without a `file_id`); it extracts the email's inner file attachment,
 * which lives in its OWN temp folder. So every file derived from an email — the
 * `.eml` and each inner attachment — must carry its own copy of this sidecar,
 * or the email communication-log step can never find it. Owns the
 * `ParsedEmailSidecar` shape shared by the upload path and the inventory reader.
 */
import { ref, uploadBytes } from 'firebase/storage';
import { pathForStage } from '#types';
import { storage } from '../services/startStorage';

export interface ParsedEmailSidecar {
    subject: string;
    from?: string;
    to?: string[];
    cc?: string[];
    bcc?: string[];
    /** ISO-8601 string (the parsed email Date, serialized). */
    date?: string;
    headers?: Record<string, string>;
    body: string;
}

export async function writeParsedEmailSidecar(
    fileFolder: string,
    sidecar: ParsedEmailSidecar,
): Promise<void> {
    const sidecarRef = ref(storage, pathForStage(fileFolder, 'parsedEmail', {}));
    const bytes = new TextEncoder().encode(JSON.stringify(sidecar));
    await uploadBytes(sidecarRef, bytes, { contentType: 'application/json' });
}
